import { useI18n } from "providers/I18nProvider";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef } from "react";

import { MessageFromEnum, MessageStatusEnum, StreamState } from "types";
import { ThreadMessage } from "..";

interface UseStreamPollingParams {
  threadId: string | null;
  processingMessageId: string | null;
  getStreamState: (workflowId: string) => Promise<StreamState | null>;
  setMessages: Dispatch<SetStateAction<ThreadMessage[]>>;
  setIsThinking: (value: boolean) => void;
  setStreamingState: Dispatch<SetStateAction<StreamState | null>>;
  syncThreadMessages: (threadId: string) => Promise<boolean>;
}

export function useStreamPolling({
  threadId,
  processingMessageId,
  getStreamState,
  setMessages,
  setIsThinking,
  setStreamingState,
  syncThreadMessages,
}: UseStreamPollingParams) {
  const { t } = useI18n();
  const pollingRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);

  const stopPolling = useCallback(
    async (opts?: { sync?: boolean }) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      pollingRef.current = false;
      setIsThinking(false);
      setStreamingState(null);

      if (opts?.sync && threadId) {
        await syncThreadMessages(threadId);
      }
    },
    [setIsThinking, setStreamingState, syncThreadMessages, threadId],
  );

  useEffect(() => {
    if (!threadId || !processingMessageId) {
      pollingRef.current = false;
      return;
    }

    pollingRef.current = true;
    const abortController = new AbortController();
    abortControllerRef.current = abortController;
    const signal = abortController.signal;

    const workflowId = `chatAIWorkflow-${processingMessageId}`;

    const poll = async (): Promise<void> => {
      try {
        setIsThinking(true);

        while (!signal.aborted) {
          try {
            const streamState = await getStreamState(workflowId);
            if (signal.aborted) return;
            if (streamState) {
              setStreamingState(streamState);
              setMessages((prev) => {
                const hasAssistant = prev.some(
                  (message) =>
                    message.from === MessageFromEnum.Assistant &&
                    message.status === MessageStatusEnum.Processing,
                );
                if (!hasAssistant) {
                  return [
                    ...prev,
                    {
                      id: `temp-assistant-${Date.now()}`,
                      from: MessageFromEnum.Assistant,
                      content: "",
                      status: MessageStatusEnum.Processing,
                      createdAt: new Date().toISOString(),
                      metadata: null,
                      threadId: threadId ?? "temp",
                    } as ThreadMessage,
                  ];
                }
                return prev;
              });

              if (streamState.isComplete) {
                setIsThinking(false);
                setStreamingState(null);
                await syncThreadMessages(threadId);
                pollingRef.current = false;
                return;
              }
              await new Promise((resolve) => setTimeout(resolve, 1500));
            } else {
              setIsThinking(false);
              await syncThreadMessages(threadId);
              pollingRef.current = false;
              return;
            }
          } catch (error) {
            if (signal.aborted) return;
            pollingRef.current = false;
            setIsThinking(false);
            const synced = await syncThreadMessages(threadId);
            if (!synced) {
              setMessages((prev) =>
                prev.map((message) => {
                  if (
                    message.from === MessageFromEnum.User &&
                    message.status === MessageStatusEnum.Processing
                  ) {
                    return {
                      ...message,
                      status: MessageStatusEnum.Failed,
                      error: t("apps.admin.chatAI.messages.errors.unexpected"),
                    };
                  }
                  return message;
                }),
              );
            }
            return;
          }
        }
      } catch (error) {
        pollingRef.current = false;
        setIsThinking(false);
      }
    };

    void poll();

    return () => {
      void stopPolling();
    };
  }, [threadId, processingMessageId]);

  return { stopPolling };
}
